import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Form,
  Input,
  Button,
  Toast,
  NavBar,
  SafeArea,
  PasscodeInput,
} from 'antd-mobile';
import { EyeInvisibleOutline, EyeOutline } from 'antd-mobile-icons';
import { authApi } from '@/api/auth';
import { useAuthStore } from '@/stores/authStore';
import type { LoginRequest } from '@shared/types';

export default function LoginPage() {
  const navigate = useNavigate();
  const { setAuth } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [form] = Form.useForm();

  const handleSubmit = async (values: LoginRequest) => {
    setLoading(true);
    try {
      const res = await authApi.login(values);
      const { data } = res.data;

      if (!data) {
        Toast.show({ icon: 'fail', content: '登录失败，请重试' });
        return;
      }

      // 存储认证信息（setAuth 内部已同步 localStorage）
      setAuth(data.user, data.token, data.refresh_token);

      Toast.show({ icon: 'success', content: `欢迎回来，${data.user.real_name}` });
      navigate('/', { replace: true });
    } catch (err: any) {
      const msg = err.response?.data?.message || '网络异常，请检查连接';
      Toast.show({ icon: 'fail', content: msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <SafeArea position="top" />

      {/* 顶部装饰区域 */}
      <div className="login-header">
        <div className="login-logo">
          <img src="/logo.png" alt="logo" className="logo-icon" style={{ width: 48, height: 48, objectFit: 'contain', display: 'block' }} />
          <h1 className="logo-title">现场智管</h1>
          <p className="logo-subtitle">现场问题 · 智能管理</p>
        </div>
      </div>

      {/* 登录表单 */}
      <div className="login-form-wrapper">
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          footer={
            <Button
              block
              type="submit"
              size="large"
              loading={loading}
              className="login-submit-btn"
            >
              {loading ? '登录中…' : '登 录'}
            </Button>
          }
          className="login-form"
        >
          <Form.Item
            name="username"
            label="用户名"
            rules={[
              { required: true, message: '请输入用户名' },
              { min: 1, message: '用户名不能为空' },
              { max: 50, message: '用户名过长' },
            ]}
          >
            <Input
              placeholder="请输入用户名"
              clearable
              autoComplete="username"
              className="login-input"
            />
          </Form.Item>

          <Form.Item
            name="password"
            label="密码"
            rules={[
              { required: true, message: '请输入密码' },
              { min: 6, message: '密码至少6位' },
            ]}
          >
            <div className="password-input-wrapper">
              <Input
                placeholder="请输入密码"
                type={visible ? 'text' : 'password'}
                autoComplete="current-password"
                className="login-input"
                clearable
              />
              <div
                className="password-eye"
                onClick={() => setVisible(!visible)}
              >
                {visible ? <EyeOutline /> : <EyeInvisibleOutline />}
              </div>
            </div>
          </Form.Item>
        </Form>

        {/* 快捷登录提示 */}
        <div className="login-tips">
          <p>测试账号：</p>
          <div className="demo-accounts">
            <span
              className="demo-tag"
              onClick={() => {
                form.setFieldsValue({ username: 'admin', password: 'admin123' });
              }}
            >
              管理员
            </span>
            <span
              className="demo-tag"
              onClick={() => {
                form.setFieldsValue({ username: 'inspector1', password: 'admin123' });
              }}
            >
              巡检员
            </span>
            <span
              className="demo-tag"
              onClick={() => {
                form.setFieldsValue({ username: 'responder1', password: 'admin123' });
              }}
            >
              责任人
            </span>
          </div>
        </div>
      </div>

      {/* 底部版权 */}
      <div className="login-footer">
        <p>? 2026 现场智管 v1.0</p>
      </div>

      <SafeArea position="bottom" />

      <style>{`
        .login-page {
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          background: linear-gradient(180deg, #2E5BFF 0%, #6B8DFF 40%, #F5F7FA 40%);
        }

        .login-header {
          padding: 48px 24px 32px;
          text-align: center;
        }

        .login-logo {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
        }

        .logo-icon {
          width: 72px;
          height: 72px;
          background: white;
          border-radius: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 36px;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
          margin-bottom: 8px;
        }

        .logo-title {
          color: white;
          font-size: 24px;
          font-weight: 700;
          margin: 0;
          letter-spacing: 2px;
        }

        .logo-subtitle {
          color: rgba(255, 255, 255, 0.85);
          font-size: 14px;
          margin: 0;
        }

        .login-form-wrapper {
          flex: 1;
          background: #F5F7FA;
          padding: 24px 20px;
          border-radius: 24px 24px 0 0;
          margin-top: -8px;
        }

        .login-form {
          --adm-color-primary: #2E5BFF;
        }

        .login-form .adm-list-item-content-prefix {
          width: 0;
        }

        .login-input {
          --font-size: 16px;
        }

        .password-input-wrapper {
          position: relative;
        }

        .password-eye {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #9CA3AF;
          font-size: 20px;
          z-index: 2;
          padding: 4px;
        }

        .login-submit-btn {
          margin-top: 24px;
          border-radius: 12px;
          font-size: 16px;
          font-weight: 600;
          height: 48px;
        }

        .login-submit-btn.adm-button-primary {
          background: linear-gradient(135deg, #2E5BFF 0%, #6B8DFF 100%);
          border: none;
        }

        .login-tips {
          margin-top: 24px;
          text-align: center;
        }

        .login-tips p {
          font-size: 13px;
          color: #9CA3AF;
          margin-bottom: 12px;
        }

        .demo-accounts {
          display: flex;
          justify-content: center;
          gap: 12px;
          flex-wrap: wrap;
        }

        .demo-tag {
          display: inline-block;
          padding: 6px 16px;
          background: white;
          border: 1px solid #e0e0e0;
          border-radius: 20px;
          font-size: 13px;
          color: #6B7280;
          cursor: pointer;
          transition: all 0.2s;
        }

        .demo-tag:active {
          background: #2E5BFF;
          color: white;
          border-color: #2E5BFF;
          transform: scale(0.96);
        }

        .login-footer {
          padding: 16px;
          text-align: center;
        }

        .login-footer p {
          font-size: 12px;
          color: #bbb;
          margin: 0;
        }

        /* 适配 iPhone 刘海屏 */
        @supports (padding-top: env(safe-area-inset-top)) {
          .login-header {
            padding-top: calc(48px + env(safe-area-inset-top));
          }
        }
      `}</style>
    </div>
  );
}